import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Alert, Table, Form, Pagination, InputGroup, Row, Col } from "react-bootstrap";
import { IconCaretLeft, IconSearch, IconCalendarStats, IconTimelineEvent, IconChartHistogram, IconCalendar } from "@tabler/icons-react";
import { Helmet } from "react-helmet-async";
import ReactApexChart from "react-apexcharts";

const GooglebotLogs = () => {
  // ---------- state ----------
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ today: 0, yesterday: 0, week: 0, month: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [selectedLogs, setSelectedLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // date filters for table (reserved for future use / compatibility)
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // chart range (default: last 30 days)
  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const thirtyDaysAgoISO = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - 29); return d.toISOString().slice(0, 10);
  }, []);

  const [chartStart, setChartStart] = useState(thirtyDaysAgoISO);
  const [chartEnd, setChartEnd] = useState(todayISO);
  const [dailySeries, setDailySeries] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);

  const navigate = useNavigate();

  // ---------- helpers ----------
  const getPaginationPages = (current, total) => {
    if (total < 1) return [];
    const delta = 2;
    const pages = new Set([1, total]);
    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < total) pages.add(i);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      result.push(sorted[i]);
      if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) result.push("...");
    }
    return result;
  };

  const startIdx = totalCount === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const endIdx   = totalCount === 0 ? 0 : Math.min(currentPage * perPage, totalCount);
  const pagesToShow = getPaginationPages(currentPage, totalPages);
  const showPagination = totalCount > 0 && pagesToShow.length > 0;

  // ---------- data fetch ----------
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage, currentPage, searchQuery]);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchDaily();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartStart, chartEnd]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      let url = `/api/googlebot-logs?limit=${perPage}&page=${currentPage}`;
      if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;

      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) {
        if (response.status === 401) { navigate("/login"); return; }
        throw new Error("Failed to load logs");
      }
      const data = await response.json();
      const total = Number(data.total_count || 0);
      setLogs(Array.isArray(data.logs) ? data.logs : []);
      setTotalCount(total);
      const pages = Math.ceil((total || 0) / perPage);
      setTotalPages(pages);
      if (pages > 0 && currentPage > pages) setCurrentPage(pages);
      if (pages === 0 && currentPage !== 1) setCurrentPage(1);
    } catch (e) {
      console.error(e);
      setError("Error loading data");
      setLogs([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [perPage, currentPage, startDate, endDate, searchQuery, navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/api/googlebot-stats", { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error("Failed to load stats");
      const data = await response.json();
      setStats({
        today: data?.today ?? 0,
        yesterday: data?.yesterday ?? 0,
        week: data?.week ?? 0,
        month: data?.month ?? 0,
      });
    } catch (error) {
      console.error("Stats load error:", error);
      setStats({ today: 0, yesterday: 0, week: 0, month: 0 });
    }
  }, []);

  const fetchDaily = useCallback(async () => {
    setChartLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ start_date: chartStart, end_date: chartEnd });
      const response = await fetch(`/api/googlebot-logs/daily?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Failed to load daily data");
      const data = await response.json(); // [{date: 'YYYY-MM-DD', count: N}, ...]
      const series = [{
        name: "Googlebot visits",
        data: data.map(d => [new Date(d.date + "T00:00:00").getTime(), d.count])
      }];
      setDailySeries(series);
    } catch (err) {
      console.error("Daily load error:", err);
      setDailySeries([{ name: "Googlebot visits", data: [] }]);
    } finally {
      setChartLoading(false);
    }
  }, [chartStart, chartEnd]);

  // ---------- actions ----------
  const handleDeleteOlderThan3Months = async () => {
    const confirmed = window.confirm("Delete all records older than 3 months?");
    if (!confirmed) return;
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/api/googlebot-logs/older-than-3-months", {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to delete old logs");
      alert("Old records successfully deleted.");
      fetchData();
      fetchStats();
      fetchDaily();
    } catch (e) {
      alert("Error: " + e.message);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedLogs.length === 0) return;
    const isConfirmed = window.confirm("Are you sure you want to delete selected records?");
    if (!isConfirmed) return;
    try {
      const token = localStorage.getItem("token");
      const ids = selectedLogs.map(Number);
      const response = await fetch("/api/googlebot-logs", {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(ids),
      });
      if (!response.ok) throw new Error("Failed to delete records");
      alert("Selected records deleted.");
      setSelectedLogs([]);
      fetchData();
      fetchStats();
      fetchDaily();
    } catch (error) {
      console.error("Delete error:", error);
      alert("Delete error: " + error.message);
    }
  };

  // ---------- UI helpers ----------
  const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="col-12 col-sm-6 col-md-3">
      <div className="card">
        <div className="card-body py-3">
          <div className="d-flex align-items-center">
            <div className="me-3">
              <span className={`avatar avatar-sm bg-${color}-lt`}>
                <Icon size={18} className={`text-${color}`} />
              </span>
            </div>
            <div className="d-flex flex-column w-100">
              <div className="text-secondary">{title}</div>
              <div className="h2 m-0">{value}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const chartOptions = useMemo(() => ({
    chart: {
      type: "area",
      height: 320,
      toolbar: { show: true, tools: { pan: true, zoom: true, reset: true } },
      zoom: { enabled: true }
    },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2 },
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 0.6, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 90, 100] }
    },
    xaxis: {
      type: "datetime",
      labels: { datetimeUTC: false }
    },
    yaxis: {
      labels: { formatter: (v) => Math.round(v) }
    },
    tooltip: {
      shared: true,
      x: { format: "yyyy-MM-dd" }
    },
    grid: { strokeDashArray: 3 }
  }), []);

  const setQuickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    setChartStart(start.toISOString().slice(0, 10));
    setChartEnd(end.toISOString().slice(0, 10));
  };

  // ---------- render ----------
  return (
    <>
      <Helmet><title>Googlebot Logs - IndexBooster</title></Helmet>

      <Card>
        {error && <Alert variant="danger">{error}</Alert>}
        <Card.Header className="d-flex align-items-center gap-2">
          <div className="d-flex align-items-center gap-2 flex-grow-1">
            <Button variant="light" size="sm" onClick={() => navigate(-1)}>
              <IconCaretLeft size={18} />
            </Button>
            <h3 className="card-title m-0">Googlebot Logs</h3>
          </div>
        </Card.Header>

        {/* Stats row (Tabler style) */}
        <div className="px-3 pb-2 pt-3">
          <div className="row g-2">
            <StatCard title="Today"        value={stats.today}     icon={IconCalendarStats} color="primary" />
            <StatCard title="Yesterday"    value={stats.yesterday} icon={IconCalendar}      color="secondary" />
            <StatCard title="Last 7 days"  value={stats.week}      icon={IconTimelineEvent} color="success" />
            <StatCard title="Last 30 days" value={stats.month}     icon={IconChartHistogram} color="warning" />
          </div>
        </div>

        {/* Chart card */}
        <div className="px-3 pb-3">
          <div className="card">
            <div className="card-header d-flex flex-column flex-md-row align-items-md-center gap-2">
              <div className="card-title m-0">Daily Googlebot Visits</div>
              <div className="ms-md-auto d-flex align-items-center gap-2 flex-wrap">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-secondary">From</span>
                  <Form.Control
                    type="date"
                    size="sm"
                    style={{ maxWidth: 160 }}
                    value={chartStart}
                    onChange={(e) => setChartStart(e.target.value)}
                  />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-secondary">To</span>
                  <Form.Control
                    type="date"
                    size="sm"
                    style={{ maxWidth: 160 }}
                    value={chartEnd}
                    onChange={(e) => setChartEnd(e.target.value)}
                  />
                </div>
                <div className="vr d-none d-md-block" />
                <div className="btn-list">
                  <Button variant="light" size="sm" onClick={() => setQuickRange(7)}>7d</Button>
                  <Button variant="light" size="sm" onClick={() => setQuickRange(30)}>30d</Button>
                  <Button variant="light" size="sm" onClick={() => setQuickRange(90)}>90d</Button>
                  <Button variant="outline-secondary" size="sm" onClick={() => { setChartStart(thirtyDaysAgoISO); setChartEnd(todayISO); }}>Reset</Button>
                </div>
              </div>
            </div>
            <div className="card-body">
              {chartLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status" />
                  <div className="mt-2 text-muted">Loading chart...</div>
                </div>
              ) : (
                <ReactApexChart options={chartOptions} series={dailySeries} type="area" height={320} />
              )}
            </div>
          </div>
        </div>

        {/* Search bar area for table */}
        <Card.Body className="border-bottom py-3">
          <div className="d-flex justify-content-start">
            <InputGroup style={{ maxWidth: 360 }}>
              <InputGroup.Text><IconSearch size={18} /></InputGroup.Text>
              <Form.Control
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => { setCurrentPage(1); setSearchQuery(e.target.value); }}
              />
            </InputGroup>
          </div>
        </Card.Body>

        {/* Table */}
        <div className="table-responsive">
          <Table className="table card-table table-vcenter datatable text-center">
            <thead>
              <tr>
                <th className="w-1">
                  <Form.Check
                    type="checkbox"
                    onChange={(e) => setSelectedLogs(e.target.checked ? logs.map((l) => l.id) : [])}
                    checked={logs.length > 0 && selectedLogs.length === logs.length}
                    disabled={logs.length === 0}
                  />
                </th>
                <th className="text-start" style={{ minWidth: 300 }}>URL</th>
                <th>Date</th>
                <th>IP</th>
                <th style={{ minWidth: 375 }}>User-Agent</th>
                <th>Redirect</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-5">
                    <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }} role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-2 text-muted">Loading logs...</div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-4">No Data</td></tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedLogs.includes(log.id)}
                        onChange={(e) => {
                          setSelectedLogs((prev) => e.target.checked ? [...prev, log.id] : prev.filter((id) => id !== log.id));
                        }}
                      />
                    </td>
                    <td className="text-start" style={{ wordBreak: "break-word" }}>{log.url}</td>
                    <td>
                      <div>{new Date(log.timestamp).toLocaleDateString()}</div>
                      <div>{new Date(log.timestamp).toLocaleTimeString([], { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>
                    </td>
                    <td>{log.ip}</td>
                    <td className="text-break">{log.user_agent}</td>
                    <td>{log.redirect_to || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {/* Footer */}
        <Card.Footer className="px-4">
          <div className="d-flex flex-column gap-2 w-100">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 w-100">
              <div className="d-flex align-items-center flex-wrap gap-3">
                {showPagination && (
                  <Pagination className="m-0 flex-wrap">
                    <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                      «
                    </Pagination.Prev>
                    {pagesToShow.map((page, i) =>
                      page === "..." ? (
                        <Pagination.Ellipsis key={`ellipsis-${i}`} disabled />
                      ) : (
                        <Pagination.Item key={`page-${page}`} active={page === currentPage} onClick={() => setCurrentPage(page)}>
                          {page}
                        </Pagination.Item>
                      )
                    )}
                    <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                      »
                    </Pagination.Next>
                  </Pagination>
                )}
                <div className="text-muted">
                  Showing <b>{startIdx}</b>–<b>{endIdx}</b> of <b>{totalCount}</b> entries
                </div>
              </div>
              <div className="d-flex align-items-center gap-2 ms-md-auto">
                <span className="text-muted">Show</span>
                <Form.Select
                  value={perPage}
                  onChange={(e) => { setCurrentPage(1); setPerPage(Number(e.target.value)); }}
                  size="sm"
                >
                  {[10, 25, 50, 100, 200, 500].map((val) => (
                    <option key={val} value={val}>{val}</option>
                  ))}
                </Form.Select>
                <span className="text-muted">entries</span>
              </div>
            </div>
            <div className="d-flex flex-column flex-sm-row gap-2">
              <Button variant="danger" onClick={handleDeleteSelected} disabled={selectedLogs.length === 0}>Remove</Button>
              <Button variant="outline-danger" onClick={handleDeleteOlderThan3Months}>Remove 3 months+</Button>
            </div>
          </div>
        </Card.Footer>
      </Card>
    </>
  );
};

export default GooglebotLogs;
